import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LEGAL_NAV_LINKS } from "./legalNav";
import "./LegalDocumentLayout.css";
import Seo from "../../Components/Seo/Seo";
import { useSiteSeo } from "../../context/SiteSeoContext";
import { truncateMetaDescription } from "../../utils/seoText";
import { usePageSeo } from "../../hooks/usePageSeo";
import { STORE_PHONE_PRIMARY, STORE_PHONE_ALT } from "../../constants/storeContact";
import { useManagedPageContent } from "../../hooks/useManagedPageContent";
import BlockRenderer from "../../Components/BlockRenderer/BlockRenderer";

/** Shared shell for Terms + standalone policies (PSP / legal visibility). */
export default function LegalDocumentLayout({ title, metaDescription, slug, children }) {
  const { pathname } = useLocation();
  const siteCtx = useSiteSeo();
  const derivedSlug = slug || (pathname ? pathname.replace(/^\//, '') : 'terms');
  const { seo_title: pageSeoTitle, seo_description: pageSeoDesc, og_image_url: pageOgImage } = usePageSeo(derivedSlug);
  const { page, blocks } = useManagedPageContent(derivedSlug);

  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  const descRaw =
    pageSeoDesc ||
    metaDescription ||
    `${title} for Gem Fashion Studios designer clothing rental. Address, refunds, GST, Razorpay, and customer duties.`;

  return (
    <div className="legalDocPage">
      <Seo
        title={pageSeoTitle || `${title}${titleSuffix}`}
        description={truncateMetaDescription(descRaw)}
        canonicalPath={pathname || "/terms"}
        ogImage={pageOgImage || undefined}
      />
      <nav className="legalDocNav" aria-label="Legal and policy documents">
        {LEGAL_NAV_LINKS.map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            className={pathname === to ? "legalDocNavLink is-active" : "legalDocNavLink"}
          >
            {label}
          </Link>
        ))}
        <Link to="/contact" className="legalDocNavLink">
          Contact
        </Link>
      </nav>

      <article className="legalDocArticle">
        <h1 className="legalDocTitle">{page?.title || title}</h1>
        {!blocks && <><p className="legalDocMerchant">
          <strong>Gem Fashion Studios</strong> — GEM House, 5/54, RSC-2, Four Bungalows, Mhada Rd, SV Patel
          Nagar, Andheri West, Mumbai, Maharashtra 400053 ·{" "}
          <a href="mailto:gemboutiqueinquiries@gmail.com">gemboutiqueinquiries@gmail.com</a> ·{" "}
          <a href={`tel:${STORE_PHONE_PRIMARY.tel}`}>{STORE_PHONE_PRIMARY.display}</a>
          {" · "}
          <a href={`tel:${STORE_PHONE_ALT.tel}`}>{STORE_PHONE_ALT.display}</a>
        </p>
        <p className="legalDocDisclaimer">
          The text below is a structured draft for storefront and payment‑partner disclosures. Review with your counsel
          and replace with wording that fits your contracts, tax posture, and operations.
        </p></>}
        <div className="legalDocBody">
          {blocks ? <BlockRenderer content={blocks} /> : children}
        </div>
      </article>
    </div>
  );
}
